import React, { useEffect } from "react";
import { ChevronLeft, ChevronRight, X } from "lucide-react";

export function PageHero({ eyebrow, title, accent = "amber" }) {
  const accentMap = {
    amber: "from-[#003C6C] to-[#0a4d8f]",
    teal: "from-teal-500 to-blue-600",
    coral: "from-rose-500 to-orange-500",
    blue: "from-[#003C6C] to-[#0a4d8f]",
    pink: "from-pink-500 to-purple-600",
  };
  return (
    <section
      className={`relative bg-gradient-to-br ${
        accentMap[accent] || accentMap.amber
      } text-white py-14 md:py-20 overflow-hidden`}
      data-testid="poly-page-hero"
    >
      <div className="absolute inset-0 opacity-20">
        <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-white blur-3xl" />
        <div className="absolute -bottom-32 -left-32 w-80 h-80 rounded-full bg-white blur-3xl" />
      </div>
      <div className="relative max-w-7xl mx-auto px-6">
        {eyebrow && (
          <div className="text-[10px] tracking-[0.4em] uppercase text-white/80 font-semibold">
            {eyebrow}
          </div>
        )}
        <h1 className="mt-2 font-serif text-4xl md:text-6xl tracking-tight">{title}</h1>
      </div>
    </section>
  );
}

export function PageBody({ children, className = "" }) {
  return (
    <section className={`max-w-7xl mx-auto px-6 py-12 md:py-16 ${className}`}>{children}</section>
  );
}

export function Lightbox({ photos, idx, onIdx, onClose }) {
  const cur = photos[idx];
  useEffect(() => {
    const h = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") onIdx((idx - 1 + photos.length) % photos.length);
      if (e.key === "ArrowRight") onIdx((idx + 1) % photos.length);
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [idx, photos.length, onClose, onIdx]);
  if (!cur) return null;
  return (
    <div
      className="fixed inset-0 z-50 bg-black/85 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="poly-lightbox"
    >
      <button
        onClick={onClose}
        className="absolute top-5 right-5 text-white/80 hover:text-white p-2"
        aria-label="Close"
      >
        <X size={22} />
      </button>
      {photos.length > 1 && (
        <>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onIdx((idx - 1 + photos.length) % photos.length);
            }}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-white/80 hover:text-white p-3 bg-black/30 rounded-sm"
            aria-label="Previous"
          >
            <ChevronLeft size={24} />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onIdx((idx + 1) % photos.length);
            }}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-white/80 hover:text-white p-3 bg-black/30 rounded-sm"
            aria-label="Next"
          >
            <ChevronRight size={24} />
          </button>
        </>
      )}
      <figure className="max-w-5xl max-h-full" onClick={(e) => e.stopPropagation()}>
        <img
          src={cur.image_url || cur.url}
          alt={cur.caption || ""}
          className="max-w-full max-h-[80vh] object-contain"
        />
        {cur.caption && (
          <figcaption className="mt-3 text-center text-white/90 text-sm">
            {cur.caption}
          </figcaption>
        )}
      </figure>
    </div>
  );
}

export function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso + (iso.length === 10 ? "T00:00:00" : "")).toLocaleDateString(undefined, {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

export function slugify(s) {
  return (s || "")
    .toString()
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
}


// Lightweight renderer for institute Vision/Mission/About content (HTML or plain text).
export function renderInstituteHtml(content) {
  if (!content) return null;
  const isHtml = /<\/?[a-z][\s\S]*?>/i.test(content);
  return isHtml
    ? content
    : content.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/\n/g, "<br/>");
}

export const STATIC_CONTENT = {
  about_gsm: `Gramaudyogik Shikshan Mandal, popularly known as GSM, is the parent educational trust established in 1975 under the Bombay Public Trust Act and also registered under the Societies Registration Act. Over the decades, GSM has emerged as a reputed educational organization committed to providing quality technical and professional education in India. The organization has grown organically across three major regions of India — Maharashtra (Western India), Delhi (National Capital Region), and Uttar Pradesh (Northern India). Presently, GSM manages a network of educational institutions and learning centres at multiple locations across the country.`,
  about_mit: `MIT Institute of Emerging Science and Technology (MITEST) is a new-age institution under GSM established in 2024, dedicated to next-generation engineering disciplines. MITEST offers undergraduate programmes across emerging fields such as Artificial Intelligence, Data Science, Robotics, Cyber Security, Cloud Computing, Internet of Things, Quantum Computing, and Sustainable Energy Systems. Designed for the industry of tomorrow, the institute combines a research-led curriculum with industry partnerships, project-based learning, and innovation labs. With AICTE approval and the academic legacy of GSM behind it, MITEST aims to nurture creative, ethical, and globally competent technologists who will shape the future of science and technology.`,
  vision: `To become a leading institution in emerging science and technology that empowers learners with cutting-edge knowledge, applied research capability, and innovation-driven mindset to build a sustainable, equitable, and intelligent future.`,
  mission: `To deliver future-ready technical education through interdisciplinary curricula, immersive research, and industry-aligned project work; to cultivate critical thinking, entrepreneurship, and ethical leadership; and to develop technologists capable of designing intelligent systems that address real-world challenges.`,
  contact: {
    title: "MIT Institute of Emerging Science and Technology (Chhatrapati Sambhajinagar)",
    address:
      "MIT Campus, Beed Bypass Road, Satara Village Road, Chhatrapati Sambhajinagar – 431010, Maharashtra, INDIA",
    phones: ["0240-2375333", "0240-2375303", "0240-2375375"],
    fax: "0240-2376230",
    email: "principal.mitp@mit.asia",
    website: "http://mitest.mit.asia",
    head_office: {
      title: "MIT, Head Office",
      address:
        "MIT, Beed Bypass Road, Satara Village Road, Chhatrapati Sambhajinagar – 431010, Maharashtra State, INDIA",
      phones: ["+91 240 2375375"],
      fax: "+91 240 2375275, 2376154",
      email: "info@mit.asia",
      website: "www.mit.asia",
    },
  },
};
